/**
 * tse:diag_state (+ the category bundle) -> the render state of the vendored POC panel (window.Rhs4.render).
 *
 * The POC computed its panel state in the browser from /api/chat responses; in V5 the server computes it
 * and pushes an allowlisted snapshot (tse_diag_state.schema.json). This maps that snapshot onto the field
 * names rhs4*.js read: cause clues -> card `matched` (Evidence and Graph tabs), question_scores -> plan
 * question_table / k1 / k2 (Questions tab), turns -> v5Turns (Questions tab, per-turn matrix + breakdown, read by the
 * V5 Rhs4QTurns.load), turn_log -> trace (Details tab), cause fix -> fix_label, cause explain -> card explain / trust / source
 * ('How N% was reached' and track record popovers, rhs4_explain.js), last.moved -> card last_move.
 * cause checks -> the leader's card / diag steps (ROOT CAUSE card), asked -> askedLog (Learning tab 'Question order'),
 * learning.last_learned -> lastLearned (Learning tab 'Last case kept').
 * Payloads stored before these fields existed map to empty values. Cause ids are PC_<n> everywhere (the POC used PAT_).
 */

const PCT_SCALE = 100;
const DECISION_BY_PHASE = Object.freeze({ LIKELY_CAUSE: "propose", HANDOFF: "handoff", ESCALATE: "handoff" });
const OUTCOME_HANDOFF = new Set(["not_resolved", "escalated"]);
const TAUGHT_STATES = new Set(["taught", "awaiting_fix", "escalated"]);
const RULED_STATUSES = new Set(["ruled_out", "failed"]);
const LEADING_STATUSES = new Set(["leading", "confirmed"]);
const FACT_VALUE_SUFFIX = Object.freeze({ no: " (no)", unknown: " (unknown)" });
const SOURCE_BOOK = "book";
const CHECK_STATUSES = new Set(["done", "failed", "current", "pending"]);
const QUESTION_ID_PREFIX = /^(q|step):/;
const ASK_SOURCE = "ask";

const list = (v) => (Array.isArray(v) ? v : []);

function factNodes(observed) {
  return list(observed).map((f) => ({
    nid: f.id,
    label: `${f.label}${FACT_VALUE_SUFFIX[f.value] || ""}`,
    type: String(f.id).startsWith("SYM_") ? "symptom" : "fact",
  }));
}

/** Bundle graph nodes plus the observed facts (labels from the snapshot win: they carry yes/no). */
function graphFor(ds, bundle) {
  const facts = factNodes(ds.observed);
  const seen = new Set(facts.map((n) => n.nid));
  const base = list(bundle?.graph?.nodes).filter((n) => !seen.has(n.nid));
  return { nodes: [...base, ...facts], edges: list(bundle?.graph?.edges) };
}

function leaderOf(causes) {
  return causes.find((c) => LEADING_STATUSES.has(c.status)) || causes.find((c) => !RULED_STATUSES.has(c.status)) || null;
}

function killsFor(ds, key) {
  return list(ds.ruled_out).filter((r) => r.cause_key === key).map((r) => ({ nid: r.by_fact || null, why: r.reason }));
}

function stepRow(ds) {
  return ds.step ? { key: ds.step.step_id, text: ds.step.text, status: "current", command: ds.step.command, why: ds.step.why } : null;
}

const normText = (t) => String(t || "").trim().toLowerCase().replace(/\s+/g, " ");

/** One row per check: the same step key, or the same check text under two keys, is shown once (first wins). */
function dedupeSteps(rows) {
  const keys = new Set();
  const texts = new Set();
  return rows.filter((r) => {
    const t = normText(r.text);
    if (keys.has(r.key) || (t && texts.has(t))) return false;
    keys.add(r.key);
    if (t) texts.add(t);
    return true;
  });
}

/**
 * Every check of the leading / confirmed cause (cause `checks`, server diag path) with the step shown now marked current,
 * so the ROOT CAUSE card keeps the checks that confirmed it. Payloads without checks fall back to the current step only.
 */
export function leaderStepsFor(ds, leader) {
  if (!leader) return [];
  const current = stepRow(ds);
  const rows = list(leader.checks)
    .filter((c) => c && c.step_id && CHECK_STATUSES.has(c.status))
    .map((c) => (current && current.key === c.step_id
      ? { ...current, text: current.text || c.text || "" }
      : { key: c.step_id, text: c.text || "", status: c.status }));
  if (current && !rows.some((r) => r.key === current.key)) rows.push(current);
  return dedupeSteps(rows);
}

/**
 * asked (answered questions, oldest first) -> the POC askedLog rows the Learning tab 'Question order' lists: each question
 * follows the answer before it (parent_label; the first follows the problem). key is the question key so keyText resolves it.
 */
export function askedLogFor(ds) {
  const items = list(ds?.asked).filter((a) => a && a.question_id);
  const ordered = items.map((a, i) => ({ a, i })).sort((x, y) => ((Number(x.a.turn) || 0) - (Number(y.a.turn) || 0)) || x.i - y.i).map((x) => x.a);
  return ordered.map((a, i) => ({
    key: String(a.question_id).replace(QUESTION_ID_PREFIX, ""),
    text: a.text || "",
    parent: null,
    parent_label: i ? ordered[i - 1].answer || null : null,
    answer: a.answer || null,
    source: ASK_SOURCE,
  }));
}

/** learning.last_learned -> the POC lastLearned record (winner, cases of that cause, undo state). */
export function lastLearnedFor(ds) {
  const ll = ds?.learning?.last_learned;
  if (!ll) return null;
  const cause = list(ds.causes).find((c) => c.key === ll.cause_key);
  const out = { payload: { winner: ll.cause_key }, status: ll.state || "", fix: ll.fix || null, at: ll.at || null };
  if (cause && Number.isFinite(Number(cause.cases)) && cause.cases !== null) out.cases = Number(cause.cases);
  return out;
}

/** Cause clues (tse:diag_state) -> POC card `matched` rows. */
export function matchedFor(cause) {
  return list(cause?.clues).map((cl) => ({
    nid: cl.fact_id, label: cl.label, seen: Number(cl.seen) || 0, wins: Number(cl.wins) || 0, is_fact: !!cl.is_fact, book: !!cl.book,
  }));
}

/** question_scores -> the plan fields rhs4.js / rhs4_qturns.js read (empty when the turn scored no question). */
export function planScoresFor(ds) {
  const qs = ds?.question_scores;
  if (!qs) return { question_table: [] };
  return { question_table: list(qs.table), k1: qs.k1, k2: qs.k2, taught_cases: qs.taught_cases };
}

/**
 * Cause explain (learning_brain_poc/score_explain.py keys, unchanged) -> the card fields rhs4_explain.js reads: `explain` for the
 * percentage popover, `trust` and `source` for the track record. A cause without explain gets explain null (the panel says so).
 */
export function explainFieldsFor(cause) {
  const e = cause?.explain;
  if (!e || typeof e !== "object") return { explain: null };
  const out = { explain: e, source: e.book ? [SOURCE_BOOK] : [] };
  if (typeof e.trust === "number") out.trust = e.trust;
  return out;
}

/** last.moved -> {cause_key: percentage points the last answer moved it}. */
export function lastMovesFor(ds) {
  const moves = {};
  list(ds?.last?.moved).forEach((m) => {
    if (m && typeof m.delta === "number" && Number.isFinite(m.delta)) moves[m.cause_key] = m.delta;
  });
  return moves;
}

function cardsFor(ds, leader) {
  const leaderSteps = leaderStepsFor(ds, leader);
  const moves = lastMovesFor(ds);
  return list(ds.causes).map((c) => ({
    pid: c.key,
    label: c.name,
    post: (Number(c.percentage) || 0) / PCT_SCALE,
    excluded: RULED_STATUSES.has(c.status),
    lods: c.lod ? [c.lod] : [],
    kills: killsFor(ds, c.key),
    matched: matchedFor(c),
    steps: leader && c.key === leader.key ? leaderSteps : [],
    reason: c.reason || null,
    fix_label: c.fix || "",
    cases: Number(c.cases) || 0,
    ...explainFieldsFor(c),
    last_move: moves[c.key] ?? null,
  }));
}

function diagFor(ds, leader) {
  if (!leader) return null;
  const confirmed = leader.status === "confirmed";
  if (!ds.step && !confirmed && !ds.pinned) return null;
  return {
    pid: leader.key,
    cause: leader.name,
    confidence: (Number(leader.percentage) || 0) / PCT_SCALE,
    identified: confirmed,
    steps: leaderStepsFor(ds, leader),
    fix: leader.fix || null,
  };
}

/** The POC handoff block: "no cause left / escalate" at the end of a diagnosis (NOT the hybrid HandoffContext). */
function handoffFor(ds, leader) {
  const ended = OUTCOME_HANDOFF.has(ds.outcome);
  const decision = DECISION_BY_PHASE[ds.phase];
  if (!ended && decision !== "handoff") return null;
  return { cause: leader?.name || null, pattern: leader?.key || null, steps: [] };
}

function lodInfo(ds) {
  const lods = list(ds.lods);
  return {
    enabled: lods.length > 0,
    definitions: lods.map((l) => ({ nid: l.id, label: l.label })),
    general: lods[0]?.id || null,
  };
}

function pcSeenFor(cards) {
  const seen = {};
  cards.forEach((c) => {
    seen[c.pid] = { pid: c.pid, label: c.label, post: c.post, excluded: c.excluded, steps: c.steps, matched: c.matched, kills: c.kills, firstTurn: 0, lastTurn: 0, current: true };
  });
  return seen;
}

/**
 * @param {object|null} ds tse:diag_state
 * @param {object|null} bundle GET /api/v5/brain/category response (kind category_bundle) or null
 * @param {{devMode?: boolean, pcApi?: {list: Function}}} [opts] pcApi = window.V4Pc (vendored chat4_pc.js)
 * @returns {object} the Rhs4.render state
 */
export function toPanelState(ds, bundle, { devMode = false, pcApi = null } = {}) {
  if (!ds) return { observed: [], v5Asked: [], devMode };
  const causes = list(ds.causes);
  const leader = leaderOf(causes);
  const cards = cardsFor(ds, leader);
  const diag = diagFor(ds, leader);
  const ruledOut = list(ds.ruled_out).map((r) => r.cause_key);
  const decision = ds.lifecycle === "ended" && ds.outcome === "resolved" ? "root_cause" : (DECISION_BY_PHASE[ds.phase] || null);
  const bundleCauses = list(bundle?.causes).map((c) => ({ pid: c.cause_key, label: c.label }));
  const known = new Set(bundleCauses.map((c) => c.pid));
  const allCauses = [...bundleCauses, ...cards.filter((c) => !known.has(c.pid)).map((c) => ({ pid: c.pid, label: c.label }))];
  const pinned = ds.pinned || null;
  const override = pinned && pinned.source === "override" ? pinned.cause_key : "";
  const pcState = { pcSeen: pcSeenFor(cards), observed: list(ds.observed).map((f) => f.id), ruledOut, ruledReason: {}, diag, step: diag && ds.step ? { pid: diag.pid, key: ds.step.step_id } : null };

  return {
    graph: graphFor(ds, bundle),
    tree: bundle?.hierarchy || null,
    causes: allCauses,
    info: { category: bundle?.label || ds.category, lod: lodInfo(ds) },
    last: { cards, plan: { phase: ds.phase, decision, ...planScoresFor(ds) }, decision, ruled_out: ruledOut, pending: [], step: stepRow(ds), questions: [] },
    observed: pcState.observed,
    asked: [],
    askedLog: askedLogFor(ds),
    diag,
    handoff: handoffFor(ds, leader),
    trace: list(ds.turn_log),
    learning: { enabled: ds.learning?.enabled !== false },
    lastLearned: lastLearnedFor(ds),
    taught: TAUGHT_STATES.has(ds.teach_status),
    resolved: ds.outcome === "resolved",
    escalated: ds.outcome === "escalated",
    round: Math.max((Number(ds.round) || 1) - 1, 0),
    ruledReason: {},
    category: ds.category,
    canOverride: !!ds.can_override,
    overridePid: override,
    overrideUndo: !!(override && pinned.undo_possible),
    pcList: pcApi && typeof pcApi.list === "function" ? pcApi.list(pcState) : [],
    focusLod: ds.focused_lod || "",
    lodFocus: null,
    mapKey: { session_id: ds.session_id, diagnosis_id: ds.diagnosis_id, seq: ds.seq },
    usage: ds.usage || null,
    mappings: list(ds.mappings),
    v5Asked: list(ds.asked),
    v5Pending: ds.pending || null,
    v5Last: ds.last || null,
    // undefined (not []) for payloads stored before `turns` existed, so the panel keeps its browser-kept history for them
    v5Turns: Array.isArray(ds.turns) ? ds.turns : undefined,
    branches: [],
    devMode,
  };
}
